<template>
  <div
    :style="{
      '--progress-bg-color': progressBgColor,
    }"
    class="relative rounded-[4px]"
  >
    <div
      class="mb-0.5 font-Kontesa text-[13px] leading-none text-navy"
      :class="{ 'animate-scale': isScalingLevel }"
    >
      LEVEL
      {{ (isNotAnimated ? level : currentLevel).toString().padStart(2, "0") }}
    </div>
    <Box
      border="lg"
      shadow="lg"
      class="relative h-[15px] overflow-hidden rounded-[4px] bg-white"
    >
      <div
        v-if="isNotAnimated"
        class="absolute left-0 top-0 h-full rounded-r-[4px] bg-[--progress-bg-color] transition-[width] duration-300 ease-linear"
        :style="{ width: `${progress * 100}%` }"
      ></div>
      <template v-for="(bar, index) in progressBars" v-else :key="index">
        <div
          class="absolute left-0 top-0 h-full rounded-r-[4px] transition-[width] duration-300 ease-linear"
          :class="{
            'bg-[--progress-bg-color]': progressBarColors[index] === 0,
            'bg-white': progressBarColors[index] === 1,
          }"
          :style="{ width: `${bar * 100}%`, zIndex: index + 1 }"
        ></div>
      </template>
    </Box>
  </div>
</template>

<script setup lang="ts">
import { theme } from "#tailwind-config";

interface ProgressBarProps {
  previousProgress?: number;
  progress: number;
  previousLevel?: number;
  level: number;
  progressBgColor?: string;
  isNotAnimated?: boolean;
}

const props = withDefaults(defineProps<ProgressBarProps>(), {
  previousProgress: 0,
  progress: 0,
  previousLevel: 0,
  level: 0,
  progressBgColor: theme.colors["green-lawn"],
});

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{ (e: "animationend"): void }>();

const progressBars = ref<number[]>([]);
const currentLevel = ref(props.previousLevel);
const progressBarColors = ref<(0 | 1)[]>([]);
const isScalingLevel = ref(false);

onMounted(() => {
  if (
    props.previousLevel === props.level &&
    props.previousProgress === props.progress
  ) {
    progressBars.value = [props.progress];
    currentLevel.value = props.level;
    progressBarColors.value = [0];
  }
});

async function playAnimation() {
  progressBars.value = [];
  const levelsToAnimate = props.level - props.previousLevel;
  let currentProgress = props.previousProgress;

  for (let i = 0; i <= levelsToAnimate; i++) progressBarColors.value.push(0);

  for (let i = 0; i < levelsToAnimate; i++) {
    await animateNewProgress(currentProgress, 1);
    currentProgress = 0;
    await delay(200);

    isScalingLevel.value = true;
    playLevelUpSound();

    await delay(500);

    currentLevel.value++;
    await delay(500);

    isScalingLevel.value = false;
    progressBarColors.value[i] = 1;
  }

  await animateNewProgress(currentProgress, props.progress);
  emit("animationend");
}

function animateNewProgress(start: number, end: number) {
  return new Promise<void>((resolve) => {
    const duration = 1000;
    const startTime = performance.now();
    const index = progressBars.value.length;
    progressBars.value.push(start);

    function step(currentTime: number) {
      const elapsedTime = currentTime - startTime;
      const progressFraction = Math.min(elapsedTime / duration, 1);
      progressBars.value[index] = start + (end - start) * progressFraction;

      if (progressFraction < 1) {
        requestAnimationFrame(step);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(step);
    playProgressSound();
  });
}

function playProgressSound() {
  const audio = new Audio("/sounds/progress-bar.mp3");
  audio.play();
}

function playLevelUpSound() {
  const audio = new Audio("/sounds/level-up.mp3");
  audio.play();
}

const delay = async (ms: number) => {
  return await new Promise((resolve) => setTimeout(resolve, ms));
};

defineExpose({ playAnimation });
</script>

<style scoped lang="css">
@keyframes scaleEffect {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.animate-scale {
  animation: scaleEffect 1s ease-in-out;
}
</style>
